import type { IFormat } from "music-metadata";

// A stale header can only over-declare by re-encoding math, not by much: the 1.25 margin absorbs frame padding and tag overhead, and tags only enlarge the file, so healthy files never trip the plausibility check
const STALE_HEADER_DURATION_TOLERANCE_RATIO = 1.25;
const PCM_CODEC = "PCM";

// music-metadata's WAV (fmt chunk) and AIFF (COMM chunk) parsers set these three fields in the same handler that names the codec PCM, so their presence is part of what PCM means here
type PcmFormat = IFormat & {
  codec: typeof PCM_CODEC;
  sampleRate: number;
  numberOfChannels: number;
  bitsPerSample: number;
};

const isPcmFormat = (format: IFormat): format is PcmFormat =>
  format.codec === PCM_CODEC &&
  format.sampleRate !== undefined &&
  format.numberOfChannels !== undefined &&
  format.bitsPerSample !== undefined;

// The byte rate is rebuilt from the fields decoders use rather than taken from music-metadata, which derives both its bitrate and its duration from the fmt chunk's blockAlign field; a WAV whose blockAlign contradicts its channel count and bit depth (seen in production: stereo 16-bit with blockAlign 2) then claims double the audio, and only a rate the decoders actually use exposes it
const getPcmBitrate = ({
  sampleRate,
  numberOfChannels,
  bitsPerSample,
}: PcmFormat): number => sampleRate * numberOfChannels * bitsPerSample;

// The plausibility check on a music-metadata duration: returns whole seconds, or null when the duration is missing or, for PCM, when the file's bytes cannot hold it. MP3s are measured by counting frames instead (getMp3DurationFromFrames), and FLAC/AAC are always decoded, so the size check only ever applies to PCM, whose byte rate is exact. The server-side lengthS producers (getAudioLengthServer/getAudioLengthBuffer) still validate on their own; adopting this helper there is a deliberate behavior change left for a follow-up.
export const getValidatedDurationS = (
  format: IFormat,
  fileSizeBytes: number
): number | null => {
  const duration = format.duration;

  if (duration === undefined || !Number.isFinite(duration) || duration <= 0) {
    return null;
  }

  if (isPcmFormat(format)) {
    // A stale WAV fact chunk from an earlier bounce can declare more audio than the bytes hold, or less when that bounce was shorter; both fail the backend's check, and a PCM decode is exact, so either direction demotes
    const impliedDurationS = (fileSizeBytes * 8) / getPcmBitrate(format);
    const isOverDeclared =
      duration > impliedDurationS * STALE_HEADER_DURATION_TOLERANCE_RATIO;
    const isUnderDeclared =
      duration * STALE_HEADER_DURATION_TOLERANCE_RATIO < impliedDurationS;

    if (isOverDeclared || isUnderDeclared) {
      return null;
    }
  }

  return Math.floor(duration);
};
